const normalizeUnit = (value) => String(value || "").trim().toLocaleLowerCase("vi");

const owns = (value, key) => Object.prototype.hasOwnProperty.call(value, key);
const providerMetadata = (item, key, meta, fallbackKey) => {
  if (owns(item, key) && item[key] !== undefined) return item[key] ?? "";
  if (item.source_batch_id && item.source_batch_id !== meta.source_batch_id) return "";
  if (normalizeUnit(item.provider_name ?? meta.provider_name) !== normalizeUnit(meta.provider_name)) return "";
  return meta[fallbackKey] ?? "";
};

export const importHeaderMetadata = (items) => {
  const item = items.find((row) => row.provider_name?.trim()) || items[0] || {};
  return { provider_name: item.provider_name?.trim() || "Nhà cung cấp",
    phone: item.provider_phone ?? "", address: item.provider_address ?? "",
    tax_code: item.provider_tax_code ?? "", invoice_number: item.invoice_number ?? "",
    source_batch_id: item.source_batch_id, notes: item.note || "" };
};

export const roundImportPrice = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? Math.round(value * 100) / 100 : 0;
  let text = String(value).trim().replace(/[₫đĐVNDvnd\s]/g, "");
  if (!text || text.startsWith("-")) return 0;
  if (text.includes(".") && text.includes(",")) {
    text = text.lastIndexOf(",") > text.lastIndexOf(".")
      ? text.replace(/\./g, "").replace(",", ".") : text.replace(/,/g, "");
  } else if (/^\d{1,3}(?:[.,]\d{3})+$/.test(text)) {
    text = text.replace(/[.,]/g, "");
  } else text = text.replace(",", ".");
  const number = Number(text);
  return Number.isFinite(number) ? Math.round(number * 100) / 100 : 0;
};

export const formatImportPrice = (value) => value === "" || value == null ? ""
  : new Intl.NumberFormat("vi-VN", { maximumFractionDigits: 2 }).format(roundImportPrice(value));

export const mapAiImportItems = ({ matched = [], unmatched = [] }, meta, createId) =>
  [...matched.map((row) => ({ ...row, is_matched: true })),
    ...unmatched.map((row) => ({ ...row, is_matched: false }))]
    .sort((a, b) => (a.source_index ?? 0) - (b.source_index ?? 0))
    .map((row) => {
      const initialRetail = row.retail_price != null && row.retail_price !== ""
        ? roundImportPrice(row.retail_price)
        : (row.matched_stock?.retail_price != null && row.matched_stock?.retail_price !== ""
            ? roundImportPrice(row.matched_stock.retail_price)
            : "");
      return {
        id: createId(),
        name: row.is_matched ? row.matched_stock?.name || row.ai_name : row.ai_name,
        barcode: row.ai_barcode || row.matched_stock?.barcode || "",
        unit: row.unit || "cái",
        quantity: row.quantity == null ? "" : row.quantity,
        price: roundImportPrice(row.price),
        retail_price: initialRetail !== "" && initialRetail > 0 ? initialRetail : "",
        retail_price_edited: false,
        provider_name: meta.providerName,
        provider_phone: meta.phone || "",
        provider_address: meta.address || "",
        provider_tax_code: meta.taxCode || "",
        invoice_number: meta.invoiceNumber || "",
        note: meta.note || "",
        expiry_date: "",
        stock_id: row.is_matched ? row.stock_id : null,
        stock_selection: row.is_matched ? "existing" : "new",
        ai_reviewed: true,
        matched_stock: row.matched_stock || null,
        match_type: row.match_type,
        match_score: row.match_score,
        is_matched: row.is_matched,
        requires_match_confirmation: (!row.is_matched && (row.candidate_suggestions || []).length > 0) ||
          (row.confidence != null && row.confidence < 0.7),
        candidate_suggestions: row.candidate_suggestions?.length ? row.candidate_suggestions
          : row.confidence != null && row.confidence < 0.7 && row.matched_stock ? [row.matched_stock] : [],
        confidence: row.confidence,
        price_source: row.price_source || "missing",
        source_name: row.ai_name,
        source_unit: row.ai_unit || row.unit,
        source_quantity: row.quantity,
        source_price: row.price,
        source_index: row.source_index,
        source_batch_id: meta.sourceId,
        source_line_total: row.ai_line_total,
      };
    });

export const selectImportStock = (item, stock, factor = 1) => {
  const differs = normalizeUnit(item.unit) !== normalizeUnit(stock.unit);
  const conversion = Number(factor);
  if (differs && (!Number.isFinite(conversion) || conversion <= 0))
    throw new Error(`Nhập số ${stock.unit} trong 1 ${item.unit} để quy đổi.`);
  if (differs && (!(Number(item.quantity) > 0) || !(roundImportPrice(item.price) > 0)))
    throw new Error("Điền số lượng và đơn giá trên chứng từ trước khi quy đổi.");
  const quantity = differs ? Math.round(Number(item.quantity) * conversion * 1000) / 1000 : item.quantity;
  const price = differs ? roundImportPrice(roundImportPrice(item.price) / conversion) : item.price;
  if (differs && (!(quantity > 0) || !(price > 0) ||
    Math.abs(quantity * price - Number(item.quantity) * roundImportPrice(item.price)) > 1))
    throw new Error("Quy đổi làm lệch thành tiền quá 1 đ; cần kiểm tra lại hệ số và đơn giá.");
  const stockRetail = stock.retail_price || stock.price_sale || stock.price;
  const newRetailPrice = !item.retail_price_edited && stockRetail && Number(stockRetail) > 0
    ? roundImportPrice(stockRetail)
    : item.retail_price;
  return { ...item, stock_id: stock.id, stock_selection: "existing", name: stock.name, unit: stock.unit,
    barcode: stock.barcode || item.barcode, quantity, price,
    retail_price: newRetailPrice,
    matched_stock: stock, is_matched: true, requires_match_confirmation: false,
    match_type: "user_confirmed", conversion_factor: differs ? conversion : undefined,
    request_id: undefined, submission_error: undefined };
};

export const reviewImportItem = (item) => {
  if (!String(item.name || "").trim()) return "Vui lòng nhập tên sản phẩm";
  if (!String(item.unit || "").trim()) return "Vui lòng nhập đơn vị tính";
  if (!Number.isFinite(Number(item.quantity)) || Number(item.quantity) <= 0) return "Số lượng phải lớn hơn 0";
  if (roundImportPrice(item.price) <= 0) return "Đơn giá phải lớn hơn 0";
  if (item.requires_match_confirmation) return "Chọn mặt hàng trong kho hoặc xác nhận tạo hàng mới";
  return "";
};

export const prepareImportSubmission = (items, meta, previous, createId) => {
  const lines = items.map((item) => ({
    stock_id: item.stock_id ? Number(item.stock_id) : null,
    stock_selection: item.stock_selection,
    source_batch_id: item.source_batch_id,
    name: String(item.name || "").trim(), stock: String(item.name || "").trim(),
    barcode: String(item.barcode || "").trim() || undefined,
    unit: String(item.unit || "").trim(), quantity: Number(Number(item.quantity).toFixed(3)),
    price: roundImportPrice(item.price),
    retail_price: item.retail_price_edited && item.retail_price !== "" ? roundImportPrice(item.retail_price) : undefined,
    paid_amount: item.paid_amount !== undefined && item.paid_amount !== "" ? roundImportPrice(item.paid_amount) : undefined,
    provider_name: item.provider_name ?? meta.provider_name,
    phone: providerMetadata(item, "provider_phone", meta, "phone"),
    address: providerMetadata(item, "provider_address", meta, "address"),
    tax_code: providerMetadata(item, "provider_tax_code", meta, "tax_code"),
    invoice_number: providerMetadata(item, "invoice_number", meta, "invoice_number"),
    note: item.note || "", notes: item.note || "", expiry_date: item.expiry_date || undefined,
  }));
  const fingerprint = JSON.stringify({ ...meta, items: lines });
  if (previous?.fingerprint === fingerprint) return previous;
  const requestId = createId();
  return { fingerprint, payload: { ...meta, request_id: requestId, submission_id: requestId,
    items: lines.map((line, index) => ({ ...line, request_id: items[index].request_id || createId() })) } };
};

export const remainingImportItems = (items, data = {}) => {
  const errors = Array.isArray(data.errors) ? data.errors : [];
  if (errors.length === 0 && data.stock_in_status === "pending_approval") return null;
  if (errors.length === 0 && (data.created_count == null || data.created_count >= items.length)) return null;
  const indices = new Set(errors.map((error) => error.index).filter((index) => Number.isInteger(index) && index >= 0 && index < items.length));
  if (!indices.size) return { items, uncertain: true };
  return { items: items.filter((_, index) => indices.has(index)).map((item) => ({ ...item,
    submission_error: errors.find((error) => error.index === items.indexOf(item))?.error || "Chưa lưu được dòng này" })), uncertain: false };
};

export const invoiceComparison = (items, invoice) => {
  const rows = invoice?.source_batch_id ? items.filter((item) => item.source_batch_id === invoice.source_batch_id) : items;
  const amount = rows.reduce((sum, item) => sum + (Number(item.quantity) || 0) * roundImportPrice(item.price), 0);
  const expected = invoice?.subtotal ?? invoice?.total_amount;
  return { amount, expected, difference: expected == null ? null : roundImportPrice(amount - expected),
    includesTax: invoice?.subtotal == null && invoice?.total_amount != null };
};

/**
 * Resize và nén ảnh trước khi gửi API AI.
 * AI không cần ảnh full-resolution — max 1600px giữ đủ chi tiết OCR/nhận diện sản phẩm
 * mà giảm payload từ 2-10 MB xuống còn ~0.3-0.8 MB.
 *
 * @param {string} dataUrl — data:image/...;base64,...
 * @param {object} [opts]
 * @param {number} [opts.maxDim=1600] — cạnh dài nhất (px)
 * @param {number} [opts.quality=0.8]  — JPEG quality 0-1
 * @returns {Promise<string>} dataUrl đã nén
 */
export const compressImageForAI = (dataUrl, { maxDim = 1600, quality = 0.8 } = {}) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      // Nếu ảnh đã nhỏ hơn ngưỡng thì chỉ cần re-encode JPEG (giảm quality)
      if (width > maxDim || height > maxDim) {
        const ratio = maxDim / Math.max(width, height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => resolve(dataUrl); // fallback: trả nguyên ảnh gốc
    img.src = dataUrl;
  });
